import { getCSSVariableByRegex } from '@primeuix/utils';
import { ContextMenu } from 'primeng/contextmenu';
import { DomHandler } from 'primeng/dom';
import { Tooltip } from 'primeng/tooltip';
import { getEffectiveZoom } from './app-zoom.util';
import {
  absoluteOverlayPosition,
  contextMenuPosition,
  domRelativeOverlayPosition,
  OverlayPosition,
  primeRelativeOverlayPosition,
} from './overlay-zoom-position';

interface ZoomHooks {
  __masofaOverlayZoomInstalled?: boolean;
  __masofaAbsolutePosition?: (element: HTMLElement, target: HTMLElement, gutter?: boolean) => void;
  __masofaRelativePosition?: (
    element: HTMLElement,
    target: HTMLElement,
    gutter?: boolean,
    fixedOrigin?: 'top' | 'bottom',
  ) => void;
}

function windowScroll(view: Window): {top: number; left: number} {
  const doc = view.document.documentElement;
  return {
    top: (view.pageYOffset || doc.scrollTop) - (doc.clientTop || 0),
    left: (view.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0),
  };
}

function viewportOf(view: Window): {width: number; height: number} {
  const doc = view.document.documentElement;
  const body = view.document.body;
  return {
    width: view.innerWidth || doc.clientWidth || body.clientWidth,
    height: view.innerHeight || doc.clientHeight || body.clientHeight,
  };
}

function overlaySize(element: HTMLElement): {width: number; height: number} {
  if (element.offsetParent) {
    return {width: element.offsetWidth, height: element.offsetHeight};
  }
  return DomHandler.getHiddenElementDimensions(element);
}

function applyGutter(element: HTMLElement, origin: OverlayPosition['origin'], gutter: boolean): void {
  if (!gutter) {
    return;
  }
  const gutterValue = getCSSVariableByRegex(/-anchor-gutter$/)?.value;
  element.style.marginTop = origin === 'bottom'
    ? `calc(${gutterValue ?? '2px'} * -1)`
    : (gutterValue ?? '');
}

function placeAbsolute(element: HTMLElement, target: HTMLElement, gutter: boolean, inline: boolean): void {
  if (!element || !target) {
    return;
  }
  const view = element.ownerDocument.defaultView ?? window;
  const rect = target.getBoundingClientRect();
  const scroll = windowScroll(view);
  const viewport = viewportOf(view);
  const overlay = overlaySize(element);
  const pos = absoluteOverlayPosition({
    zoom: getEffectiveZoom(element.ownerDocument),
    targetTop: rect.top,
    targetLeft: rect.left,
    targetWidth: target.offsetWidth,
    targetHeight: target.offsetHeight,
    overlayWidth: overlay.width,
    overlayHeight: overlay.height,
    scrollTop: scroll.top,
    scrollLeft: scroll.left,
    viewportWidth: viewport.width,
    viewportHeight: viewport.height,
  });
  element.style.top = `${pos.top}px`;
  element.style.transformOrigin = pos.origin;
  if (inline && getComputedStyle(element).direction === 'rtl') {
    element.style.insetInlineEnd = `${pos.left}px`;
  } else if (inline) {
    element.style.insetInlineStart = `${pos.left}px`;
  } else {
    element.style.left = `${pos.left}px`;
  }
  applyGutter(element, pos.origin, gutter);
}

function closestRelative(element: HTMLElement | null): HTMLElement | null {
  let current: HTMLElement | null = element;
  while (current) {
    if (getComputedStyle(current).position === 'relative') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

function placeDomRelative(element: HTMLElement, target: HTMLElement, gutter: boolean): void {
  if (!element || !target) {
    return;
  }
  const view = element.ownerDocument.defaultView ?? window;
  const scroll = windowScroll(view);
  const viewport = viewportOf(view);
  const relative = closestRelative(element);
  const relativeRect = relative?.getBoundingClientRect();
  const rect = target.getBoundingClientRect();
  const overlay = overlaySize(element);
  const pos = domRelativeOverlayPosition({
    zoom: getEffectiveZoom(element.ownerDocument),
    targetTop: rect.top,
    targetLeft: rect.left,
    targetWidth: target.offsetWidth,
    targetHeight: target.offsetHeight,
    overlayWidth: overlay.width,
    overlayHeight: overlay.height,
    relativeTop: relativeRect ? relativeRect.top : -scroll.top,
    relativeLeft: relativeRect ? relativeRect.left : -scroll.left,
    scrollTop: scroll.top,
    scrollLeft: scroll.left,
    viewportWidth: viewport.width,
    viewportHeight: viewport.height,
  });
  element.style.top = `${pos.top}px`;
  element.style.left = `${pos.left}px`;
  element.style.transformOrigin = pos.origin;
  applyGutter(element, pos.origin, gutter);
}

function placePrimeRelative(
  element: HTMLElement,
  target: HTMLElement,
  gutter: boolean,
  fixedOrigin?: 'top' | 'bottom',
): void {
  if (!element || !target) {
    return;
  }
  const view = element.ownerDocument.defaultView ?? window;
  const rect = target.getBoundingClientRect();
  const scroll = windowScroll(view);
  const viewport = viewportOf(view);
  const overlay = overlaySize(element);
  const pos = primeRelativeOverlayPosition({
    zoom: getEffectiveZoom(element.ownerDocument),
    targetTop: rect.top,
    targetLeft: rect.left,
    targetWidth: target.offsetWidth,
    targetHeight: target.offsetHeight,
    overlayWidth: overlay.width,
    overlayHeight: overlay.height,
    scrollTop: scroll.top,
    scrollLeft: scroll.left,
    viewportWidth: viewport.width,
    viewportHeight: viewport.height,
    fixedOrigin,
  });
  element.style.top = `${pos.top}px`;
  element.style.insetInlineStart = `${pos.left}px`;
  element.style.transformOrigin = pos.origin;
  applyGutter(element, pos.origin, gutter);
}

function installTooltipZoom(): void {
  const proto = Tooltip.prototype;
  const hostOffset = proto.getHostOffset;
  proto.getHostOffset = function getHostOffset(this: Tooltip) {
    const offset = hostOffset.call(this);
    const zoom = getEffectiveZoom();
    if (zoom === 1) {
      return offset;
    }
    return {left: offset.left / zoom, top: offset.top / zoom};
  };
  const outOfBounds = proto.isOutOfBounds;
  proto.isOutOfBounds = function isOutOfBounds(this: Tooltip) {
    if (getEffectiveZoom() === 1) {
      return outOfBounds.call(this);
    }
    const rect = this.container?.getBoundingClientRect();
    if (!rect) {
      return false;
    }
    return rect.left < 0 || rect.top < 0 || rect.right > window.innerWidth || rect.bottom > window.innerHeight;
  };
}

function installContextMenuZoom(): void {
  const proto = ContextMenu.prototype;
  proto.position = function position(this: ContextMenu) {
    const element = this.containerViewChild?.nativeElement as HTMLElement | undefined;
    const view = document.defaultView;
    const scrolling = document.scrollingElement;
    if (!element || !view || !scrolling) {
      return;
    }
    const width = element.offsetParent ? element.offsetWidth : DomHandler.getHiddenElementOuterWidth(element);
    const height = element.offsetParent ? element.offsetHeight : DomHandler.getHiddenElementOuterHeight(element);
    const pos = contextMenuPosition({
      pageX: this.pageX,
      pageY: this.pageY,
      width,
      height,
      scrollLeft: scrolling.scrollLeft,
      scrollTop: scrolling.scrollTop,
      viewportWidth: view.innerWidth,
      viewportHeight: view.innerHeight,
      zoom: getEffectiveZoom(),
    });
    element.style.left = `${pos.left}px`;
    element.style.top = `${pos.top}px`;
  };
}

/**
 * One install covers every PrimeNG overlay: Select, MultiSelect, DatePicker, Menu,
 * Popover, Password, ConfirmPopup and the rest of `p-overlay`, plus tooltips and
 * the right-click menu. DatePicker and Menu call `@primeuix/utils` directly; those
 * functions delegate here after `scripts/patch-overlay-zoom.mjs`.
 */
export function installOverlayZoomFix(): void {
  const hooks = globalThis as typeof globalThis & ZoomHooks;
  if (hooks.__masofaOverlayZoomInstalled) {
    return;
  }
  hooks.__masofaOverlayZoomInstalled = true;
  hooks.__masofaAbsolutePosition = (element, target, gutter = true) => placeAbsolute(element, target, gutter, true);
  hooks.__masofaRelativePosition = (element, target, gutter = true, fixedOrigin) => {
    placePrimeRelative(element, target, gutter, fixedOrigin);
  };
  DomHandler.absolutePosition = (element: HTMLElement, target: HTMLElement, gutter = true) => {
    placeAbsolute(element, target, gutter, false);
  };
  DomHandler.relativePosition = (element: HTMLElement, target: HTMLElement, gutter = true) => {
    placeDomRelative(element, target, gutter);
  };
  installTooltipZoom();
  installContextMenuZoom();
}
